import React from "react";
import { Link } from "react-router-dom";

import {
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaQuestionCircle,
} from "react-icons/fa";

import {
  useGetMyEnglishAttemptsQuery,
} from "../../slices/englishQuizAttemptApiSlice";


const EnglishQuizAttemptListScreen = () => {
  const {
    data: attempts = [],
    isLoading,
    error,
  } = useGetMyEnglishAttemptsQuery();


  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="p-10 text-center">
        Loading your attempts...
      </div>
    );
  }


  // ======================================================
  // Error
  // ======================================================

  if (error) {
    return (
      <div className="p-10 text-center text-red-600">
        Unable to load your English quiz attempts.
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gray-50 p-6">

      <div className="max-w-5xl mx-auto">

        {/* ==================================================
            Header
        ================================================== */}

        <div className="mb-6">

          <Link
            to="/student/english-quizzes"
            className="
              text-blue-600
              inline-block
              mb-4
            "
          >
            ← Back to English Quizzes
          </Link>

          <h1 className="text-3xl font-bold text-gray-800">
            My English Quiz Attempts
          </h1>

          <p className="mt-2 text-gray-600">
            View your previous English quiz attempts and results.
          </p>

        </div>


        {/* ==================================================
            Empty
        ================================================== */}

        {attempts.length === 0 && (
          <div
            className="
              rounded-2xl
              bg-white
              p-10
              shadow
              text-center
            "
          >

            <FaQuestionCircle
              className="
                mx-auto
                text-4xl
                text-gray-400
              "
            />

            <h2 className="mt-4 text-xl font-semibold">
              No attempts yet
            </h2>

            <p className="mt-2 text-gray-500">
              You have not attempted an English quiz yet.
            </p>

            <Link
              to="/student/english-quizzes"
              className="
                mt-6
                inline-block
                rounded-xl
                bg-blue-600
                px-6
                py-3
                font-semibold
                text-white
                hover:bg-blue-700
              "
            >
              View English Quizzes
            </Link>

          </div>
        )}


        {/* ==================================================
            Attempts
        ================================================== */}

        <div className="space-y-4">

          {attempts.map((attempt) => {

            const passed =
              attempt.result === "pass";

            const completed =
              attempt.status === "completed";

            return (
              <div
                key={attempt._id}
                className="
                  rounded-2xl
                  bg-white
                  p-6
                  shadow
                "
              >

                {/* ==================================================
                    Top
                ================================================== */}

                <div
                  className="
                    flex
                    flex-col
                    gap-4
                    md:flex-row
                    md:items-center
                    md:justify-between
                  "
                >

                  <div>

                    <h2 className="text-xl font-bold text-gray-800">
                      {attempt.quizName ||
                        attempt.quiz?.name ||
                        "English Quiz"}
                    </h2>

                    <p className="mt-1 text-sm text-gray-500">
                      Attempt #{attempt.attemptNumber || 1}
                    </p>

                  </div>


                  {/* Result */}

                  {completed ? (
                    <div
                      className={`
                        inline-flex
                        items-center
                        gap-2
                        rounded-full
                        px-4
                        py-2
                        text-sm
                        font-semibold
                        ${
                          passed
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }
                      `}
                    >

                      {passed ? (
                        <FaCheckCircle />
                      ) : (
                        <FaTimesCircle />
                      )}

                      {passed
                        ? "Passed"
                        : "Failed"}

                    </div>
                  ) : (
                    <div
                      className="
                        inline-flex
                        items-center
                        gap-2
                        rounded-full
                        bg-yellow-100
                        px-4
                        py-2
                        text-sm
                        font-semibold
                        text-yellow-700
                      "
                    >
                      <FaClock />
                      In Progress
                    </div>
                  )}

                </div>


                {/* ==================================================
                    Statistics
                ================================================== */}

                <div
                  className="
                    mt-5
                    grid
                    grid-cols-2
                    md:grid-cols-4
                    gap-4
                  "
                >

                  {/* Questions */}

                  <div className="rounded-xl bg-gray-50 p-4">

                    <p className="text-sm text-gray-500">
                      Questions
                    </p>

                    <p className="mt-1 text-xl font-bold">
                      {attempt.totalQuestions || 0}
                    </p>

                  </div>


                  {/* Score */}

                  <div className="rounded-xl bg-blue-50 p-4">

                    <p className="text-sm text-gray-500">
                      Score
                    </p>

                    <p className="mt-1 text-xl font-bold text-blue-700">
                      {attempt.obtainedMarks ?? 0}
                      {" / "}
                      {attempt.totalMarks ?? 0}
                    </p>

                  </div>


                  {/* Percentage */}

                  <div className="rounded-xl bg-purple-50 p-4">

                    <p className="text-sm text-gray-500">
                      Percentage
                    </p>

                    <p className="mt-1 text-xl font-bold text-purple-700">
                      {attempt.percentage ?? 0}%
                    </p>

                  </div>


                  {/* Correct */}

                  <div className="rounded-xl bg-green-50 p-4">

                    <p className="text-sm text-gray-500">
                      Correct
                    </p>

                    <p className="mt-1 text-xl font-bold text-green-700">
                      {attempt.correctAnswers ?? 0}
                    </p>

                  </div>

                </div>


                {/* ==================================================
                    Action
                ================================================== */}

                <div className="mt-5 flex justify-end">

                  {completed ? (
                    <Link
                      to={`/student/english-quiz-attempt/${attempt._id}/result`}
                      className="
                        rounded-xl
                        bg-blue-600
                        px-5
                        py-2.5
                        font-semibold
                        text-white
                        hover:bg-blue-700
                      "
                    >
                      View Result
                    </Link>
                  ) : (
                    <Link
                      to={`/student/english-quiz-attempt/${attempt._id}`}
                      className="
                        rounded-xl
                        bg-green-600
                        px-5
                        py-2.5
                        font-semibold
                        text-white
                        hover:bg-green-700
                      "
                    >
                      Resume Quiz
                    </Link>
                  )}

                </div>

              </div>
            );
          })}

        </div>

      </div>

    </div>
  );
};


export default EnglishQuizAttemptListScreen;